import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  FileCode,
  Layers,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import type { PostmarkTemplate } from './types';

/**
 * Sentinel filter value for the "No layout" chip. A real Postmark alias
 * can't collide with this because Postmark aliases are restricted to
 * letters, digits, and hyphens.
 *
 * Note: only Standard templates are ever shown in this picker because Postmark
 * can't send with a Layout-type template directly (Layouts are wrappers
 * referenced by Standards via `LayoutTemplate`). The chips below let the user
 * narrow Standards by which base layout they're built on.
 */
const NO_LAYOUT = '__none__';

/**
 * Which kind of Postmark template the picker is selecting:
 *   - `'standards'` (default) — pick a sendable template. Hides Layouts.
 *   - `'layouts'`              — pick a base Layout (by alias). Hides Standards.
 */
export type PostmarkTemplatePickerMode = 'standards' | 'layouts';

interface PostmarkTemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-fetched list passed in by the parent so we don't double-fetch. */
  templates: PostmarkTemplate[];
  loading: boolean;
  error: string | null;
  /** Allow the parent to refetch on demand. */
  onRefresh: () => void;
  /**
   * `templateAlias` here is the raw value stored on the row (template id as a
   * string OR an alias). Used to highlight the currently selected row.
   */
  currentValue: string | null;
  onSelect: (template: PostmarkTemplate) => void;
  /**
   * Pick mode. Defaults to `'standards'` (the original sendable-template flow).
   * Pass `'layouts'` to pick a base layout alias instead.
   */
  mode?: PostmarkTemplatePickerMode;
}

export default function PostmarkTemplatePickerModal({
  isOpen,
  onClose,
  templates,
  loading,
  error,
  onRefresh,
  currentValue,
  onSelect,
  mode = 'standards',
}: PostmarkTemplatePickerModalProps) {
  const [query, setQuery] = useState('');
  /**
   * Selected layout filter:
   *   - `null`      → show every sendable template (every Standard)
   *   - `NO_LAYOUT` → only Standards that don't reference a layout
   *   - any other s → only Standards whose `layoutTemplate` alias matches it
   *
   * Layouts themselves are never included in the picker's row list because
   * Postmark can't send with a Layout template.
   */
  const [layoutFilter, setLayoutFilter] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setLayoutFilter(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  /**
   * Aliased layout templates available on this server, sorted by display
   * name. We never put these in the row list — they only seed the filter
   * chips so the user can narrow Standards by their base layout.
   */
  const layouts = useMemo(
    () =>
      templates
        .filter((t) => t.templateType === 'Layout' && t.alias)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
    [templates],
  );

  // If the currently selected layout alias disappears (e.g. a refresh removed
  // it), fall back to "All" so the list isn't empty for a ghost filter.
  useEffect(() => {
    if (layoutFilter === null || layoutFilter === NO_LAYOUT) return;
    const stillExists = layouts.some((t) => t.alias === layoutFilter);
    if (!stillExists) setLayoutFilter(null);
  }, [layouts, layoutFilter]);

  const isLayoutsMode = mode === 'layouts';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) => {
      if (isLayoutsMode) {
        // Layouts mode: show only Layout-type rows. Postmark layouts are
        // referenced by alias, so we hide aliasless layouts to avoid
        // selecting something that can't be persisted.
        if (t.templateType !== 'Layout') return false;
        if (!t.alias) return false;
      } else {
        // Standards mode: layouts are never sendable, so hide them.
        if (t.templateType === 'Layout') return false;
        if (layoutFilter === NO_LAYOUT) {
          if (t.layoutTemplate) return false;
        } else if (layoutFilter !== null) {
          if (t.layoutTemplate !== layoutFilter) return false;
        }
      }
      if (!q) return true;
      const id = String(t.templateId);
      return (
        t.name.toLowerCase().includes(q) ||
        (t.alias?.toLowerCase().includes(q) ?? false) ||
        id.includes(q)
      );
    });
  }, [templates, query, layoutFilter, isLayoutsMode]);

  /**
   * Population size for the footer count and empty-state copy. In standards
   * mode this is "Standards on the server"; in layouts mode it's "aliased
   * Layouts on the server".
   */
  const populationCount = useMemo(() => {
    if (isLayoutsMode) {
      return templates.filter((t) => t.templateType === 'Layout' && t.alias).length;
    }
    return templates.filter((t) => t.templateType !== 'Layout').length;
  }, [templates, isLayoutsMode]);

  const currentTrim = currentValue?.trim() ?? '';

  const titleText = isLayoutsMode
    ? 'Choose Postmark layout'
    : 'Choose Postmark template';
  const searchPlaceholder = isLayoutsMode
    ? 'Search layouts by name, alias, or id…'
    : 'Search by name, alias, or id…';
  const emptyEmptyState = isLayoutsMode
    ? 'No layout templates found on this Postmark server.'
    : 'No sendable templates found on this Postmark server.';
  const emptyFilteredState = isLayoutsMode
    ? 'No layouts match your search.'
    : 'No templates match your search.';
  const footerNoun = isLayoutsMode ? 'layouts' : 'templates';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {isLayoutsMode ? (
              <Layers className="w-5 h-5 text-blue-500" />
            ) : (
              <FileCode className="w-5 h-5 text-blue-500" />
            )}
            {titleText}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {!isLayoutsMode && layouts.length > 0 ? (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-1">
                <Layers className="w-3 h-3" />
                Base
              </div>
              <FilterChip
                label="All"
                active={layoutFilter === null}
                onClick={() => setLayoutFilter(null)}
              />
              {layouts.map((layout) => (
                <FilterChip
                  key={layout.templateId}
                  label={
                    layout.name || layout.alias || `Layout ${layout.templateId}`
                  }
                  active={layoutFilter === layout.alias}
                  onClick={() => setLayoutFilter(layout.alias)}
                />
              ))}
              <FilterChip
                label="No layout"
                active={layoutFilter === NO_LAYOUT}
                onClick={() => setLayoutFilter(NO_LAYOUT)}
                variant="muted"
              />
            </div>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto">
          {error ? (
            <div className="p-6 text-sm text-red-700 dark:text-red-300 break-words">
              Could not load templates: {error}
            </div>
          ) : loading && populationCount === 0 ? (
            <div className="p-10 flex justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
              {populationCount === 0 ? emptyEmptyState : emptyFilteredState}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((t) => {
                const idStr = String(t.templateId);
                const isSelected =
                  currentTrim === idStr ||
                  (t.alias != null && currentTrim === t.alias);
                return (
                  <li key={t.templateId}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(t);
                        onClose();
                      }}
                      className={`w-full text-left px-6 py-3 flex items-start gap-3 transition-colors ${
                        isSelected
                          ? 'bg-emerald-50 dark:bg-emerald-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'
                      }`}
                    >
                      <div className="mt-0.5 w-4">
                        {isSelected ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {t.name || '(unnamed)'}
                          </span>
                          {!t.active ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              inactive
                            </span>
                          ) : null}
                          {!isLayoutsMode &&
                          t.templateType &&
                          t.templateType !== 'Standard' ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                              {t.templateType}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3 flex-wrap font-mono">
                          <span>id: {t.templateId}</span>
                          {t.alias ? <span>alias: {t.alias}</span> : null}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shrink-0 flex justify-between gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          <span>
            {populationCount > 0
              ? `${filtered.length} of ${populationCount} ${footerNoun}`
              : ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  variant?: 'default' | 'muted';
}

function FilterChip({
  label,
  active,
  onClick,
  variant = 'default',
}: FilterChipProps) {
  const base =
    'inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors max-w-[14rem] truncate';
  let cls: string;
  if (active) {
    cls = 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600';
  } else if (variant === 'muted') {
    cls =
      'bg-transparent text-gray-500 dark:text-gray-400 border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
  } else {
    cls =
      'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${cls}`}
      title={label}
    >
      <span className="truncate">{label}</span>
    </button>
  );
}
